import type {
  EditorPlugin,
  MarkdownConvertFunction,
} from '../../types/aha-editor';
import { Editor, Transforms } from 'slate';
import { isSelectionInList } from '../ListPlugin/utils';
import { isListItem } from '../ListPlugin/guards';
import ListWrapUtils, { type WrapInListOptions } from './ListWrapUtils';
import {
  UnorderedListComponentPlugin,
  OrderedListComponentPlugin,
  CheckboxListComponentPlugin,
} from './ListComponentsPlugin';
import MarkdownPlugin from '../MarkdownPlugin/MarkdownPlugin';

const convertToListOfType = (editor: Editor, options: WrapInListOptions) => {
  if (isSelectionInList(editor)) return false;

  ListWrapUtils.wrapInList(editor, options);
  return true;
};

const convertToChecklist: MarkdownConvertFunction = (editor, options) => {
  convertToListOfType(editor, { type: 'checklist' });

  if (!/\[x\]/i.test(options.textWithMarkdown)) return;

  // The list was created with [x] or [X], check the list item that was just
  // added.
  const listItemEntry = Editor.above(editor, { match: isListItem });
  if (!listItemEntry) return;

  const [, listItemPath] = listItemEntry;
  Transforms.setNodes(editor, { list_checked: true }, { at: listItemPath });
};

const revertList = (editor: Editor) => {
  ListWrapUtils.unwrapList(editor);
};
const isNotInList = (editor: Editor) => !isSelectionInList(editor);

export const UnorderedListMarkdownPlugin: EditorPlugin = {
  name: 'Unordered list markdown plugin',
  dependencies: [MarkdownPlugin, UnorderedListComponentPlugin],
  markdown: [
    {
      type: 'element',
      regex: /^([-*]\s)$/,
      triggerCharacter: ' ',
      convert: (editor) => convertToListOfType(editor, { type: 'ul_list' }),
      shiftTabRevert: true,
      revert: revertList,
      test: isNotInList,
    },
  ],
};

const convertToOrderedList: MarkdownConvertFunction = (editor, options) => {
  // Extract the number from the markdown (e.g., "4. " -> 4)
  // parseInt automatically ignores the period and space
  const startNumber = parseInt(options.match[1], 10);

  // Create list data with start number if it's not 1 (default)
  const listData = startNumber > 1 ? { list_start: startNumber } : {};

  return convertToListOfType(editor, { type: 'ol_list', listData });
};

export const OrderedListMarkdownPlugin: EditorPlugin = {
  name: 'Ordered list markdown plugin',
  dependencies: [MarkdownPlugin, OrderedListComponentPlugin],
  markdown: [
    {
      type: 'element',
      regex: /^(\d+\.\s)$/,
      triggerCharacter: ' ',
      convert: convertToOrderedList,
      shiftTabRevert: true,
      revert: revertList,
      test: isNotInList,
    },
  ],
};
export const CheckboxListMarkdownPlugin: EditorPlugin = {
  name: 'Checkbox list markdown plugin',
  dependencies: [MarkdownPlugin, CheckboxListComponentPlugin],
  markdown: [
    {
      type: 'element',
      triggerCharacter: ' ',
      regex: /^((?:\[\s?\]|\[x\])\s)$/i,
      convert: convertToChecklist,
      shiftTabRevert: true,
      revert: revertList,
      test: isNotInList,
    },
  ],
};

const ListMarkdownPlugins: EditorPlugin[] = [
  UnorderedListMarkdownPlugin,
  OrderedListMarkdownPlugin,
  CheckboxListMarkdownPlugin,
];

export default ListMarkdownPlugins;
